import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import DOMPurify from "dompurify";
import { Hash } from "lucide-react";
import AdminActions from "@/components/AdminActions";
import VideoEmbed from "@/components/VideoEmbed";
import { fontClass } from "@/lib/fonts";
import LangToggle from "@/components/LangToggle";
import Seo from "@/components/Seo";

export default function BlogDetail() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [lang, setLang] = useState("en");

  const load = useCallback(() => {
    api.get(`/blog/${slug}`).then(r => setPost(r.data)).catch(() => {});
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  if (!post) return <div className="min-h-screen flex items-center justify-center text-slate-200">Loading…</div>;

  const hasBn = Boolean(post.title_bn);
  const bn = lang === "bn" && hasBn;
  const title = bn ? post.title_bn : post.title;
  const excerpt = bn ? (post.excerpt_bn || post.excerpt) : post.excerpt;
  const content = bn ? (post.content_bn || post.content) : post.content;

  return (
    <div className="min-h-screen pt-32 relative overflow-hidden">
      <Seo title={post.title} description={post.excerpt} path={`/blog/${post.slug}`} type="article" />
      <div className="orb orb-violet w-[26rem] h-[26rem] -top-32 -left-32 opacity-60" aria-hidden="true" />
      <article className="max-w-3xl mx-auto px-6 md:px-10 py-12 relative">
        <Link to="/blog" className="text-xs text-slate-200 uppercase tracking-[0.2em] hover:text-orange-300 transition-colors">← All Posts</Link>

        <div className="mt-8 flex items-center justify-between gap-4">
          <div>{hasBn && <LangToggle lang={lang} setLang={setLang} />}</div>
          <AdminActions kind="blog" item={post} onChange={load} redirectAfterDelete="/blog" />
        </div>

        <div className="mt-4">
          <div className="flex items-center gap-3 text-[11px] text-slate-200 uppercase tracking-[0.15em] mb-6">
            <span>{new Date(post.published_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
            <span>·</span>
            <span>{post.read_minutes} min read</span>
            <span>·</span>
            <span>{post.author}</span>
          </div>
          <h1 className={`${bn ? "font-bn" : fontClass(post.title_font, "font-bn")} font-bold text-3xl md:text-4xl leading-snug tracking-tight text-white`}>{title}</h1>
          <p className="text-slate-200 mt-5 text-lg font-bn leading-relaxed">{excerpt}</p>
        </div>

        <div className="aspect-[16/9] overflow-hidden my-10 rounded-3xl border border-white/10">
          <img src={post.cover_image} alt="" className="w-full h-full object-cover" />
        </div>

        {post.video_url && <VideoEmbed url={post.video_url} className="mb-10" />}

        {/<[a-z][\s\S]*>/i.test(content) ? (
          <div
            className="rich-content font-bn text-slate-200 text-lg"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content) }}
            data-testid="blog-content"
          />
        ) : (
          <div className="font-bn text-slate-200 leading-[1.9] text-lg whitespace-pre-line" data-testid="blog-content">
            {content}
          </div>
        )}

        <div className="mt-14 pt-8 border-t border-white/10">
          <div className="text-[11px] uppercase tracking-[0.22em] text-slate-200 font-semibold mb-4">Tagged</div>
          <div className="flex flex-wrap gap-2">
            {post.tags.map(t => (
              <span key={t} className="bg-white/10 border border-white/10 rounded-full px-3 py-1.5 text-xs uppercase tracking-widest text-slate-200 flex items-center gap-1">
                <Hash size={10} aria-hidden="true" />{t}
              </span>
            ))}
          </div>
        </div>
      </article>
    </div>
  );
}
